import styled from "@emotion/styled/macro";
import {
  SvgIcon,
  SvgNames,
  TABLET_BREAKPOINT,
  tokens,
  Tooltip,
  Typography,
} from "@sunrun/experience-ui-components";
import { Fragment } from "react";
import { ReviewOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { type PromotionSelectionItem } from "services/promotions";
import { CardCarouselData } from "providers/Types";
import { PriceOfPowerProspect } from "hooks/usePriceOfPowerUrl";
import { GraphContent } from "components/organisms/GraphContent";
import { ProductCarousel } from "components/molecules/ProductCarousel";
import {
  AgreementDetailsAndMonthlyPayments,
  NonSolarPaymentDetails,
} from "components/organisms/OfferReviewPriceDetails";
import {
  pickDesignImageUrl,
  useStorefrontDesignImages,
} from "hooks/useStorefrontDesignImages";
import { formatCardValueWithSuffix } from "utils/formatCardValue";
import bgNewHomes from "components/assets/images/bg-new-homes.jpg";
import { OfferReviewUtilityBillUploadBanner } from "components/molecules/OfferReviewUtilityBillUploadBanner";
import { OfferReviewSquareFootageEstimateBanner } from "components/molecules/OfferReviewSquareFootageEstimateBanner";
import { TreeRemovalLegend } from "components/molecules/TreeRemovalLegend";

type OfferReviewContentProps = {
  offer: ReviewOffer;
  prospectId: string;
  prospect?: PriceOfPowerProspect;
  nonSolarView: boolean;
  hasBattery: boolean;
  isMobile: boolean;
  isCustomer: boolean;
  systemCarouselData: CardCarouselData[];
  showSolarStepsDetails: boolean;
  setShowSolarStepsDetails: (show: boolean) => void;
  onContactSalesRepClick: () => void;
  onCheckoutClick: () => void;
  setShowMapModalOpen: (show: boolean) => void;
  isTablet: boolean;
  isNewHomes?: boolean;
  showSelfSupply?: boolean;
  promotions?: PromotionSelectionItem[];
  showPromotions?: boolean;
  showUtilityBillUploadBanner?: boolean;
  showSquareFootageEstimateBanner?: boolean;
  onUtilityBillClick?: () => void;
};

type LayoutContentProps = OfferReviewContentProps;

const resolveWarrantyTooltip = (
  warranty: string,
  years: string
): string | null => {
  const warrantyText = warranty.toLowerCase();
  switch (warrantyText) {
    case "energy production guarantee":
      return "The system will produce [X]% or more of our production estimate, or we credit you for the diference.";
    case "workmanship warranty":
      return `Sunrun goes beyond what is ordered by the manufacturers and repairs or replaces the equipment as needed for ${years} years.`;
    case "equipment maintenance and repairs":
      return `If there's an issue with the installation or our work on the system, we'll fix it at no cost to you for ${years} years.`;
    case "battery replacement guarantee":
      return `If your battery stops working, we'll repair or replace it as necessary for ${years} years.`;
    case "roof penetration warranty":
      return `We guarantee that our roof penetrations are watertight for ${years} years.`;
    case "24/7 system monitoring":
      return `Our proactive system monitoring informs us of possible issues, so your system overview overview can operate at or above its Target Zone. With the Sunrun mobile app, you also have 24/7 access to your system overview overview's performance.`;
    case "award winning service and support":
      return `From a team of dedicated, on-call service agents and experienced technicians.`;
    case "guaranteed system transfer eligibility":
      return `If you move, we guarantee the next homeowner will qualify to assume the agreement.`;
    default:
      return null;
  }
};

type WarrantyListItemProps = {
  title: string;
  termYears: string;
  isMobile: boolean;
  isTablet: boolean;
};

const WarrantyListItem = ({
  title,
  termYears,
  isMobile,
  isTablet,
}: WarrantyListItemProps) => {
  const tooltipContent = resolveWarrantyTooltip(title, termYears);

  return (
    <WarrantyItem isMobile={isMobile}>
      <WarrantyItemContent>
        <SvgIcon
          name={SvgNames.SuccessCircle}
          width={24}
          height={24}
          color={tokens.BRAND_HERO_BLUE}
        />
        <Typography size={16} color={tokens.BRAND_HERO_BLUE}>
          {termYears} {termYears === "1" ? "year" : "years"} {title}
        </Typography>
      </WarrantyItemContent>
      {tooltipContent && (
        <WarrantyTooltip content={tooltipContent} direction={"top-right"}>
          <SvgIcon
            name={SvgNames.InfoCircle}
            width={14}
            height={14}
            color={tokens.BRAND_HERO_BLUE}
          />
        </WarrantyTooltip>
      )}
    </WarrantyItem>
  );
};

const SolarNewLayoutContent = ({
  offer,
  hasBattery,
  isMobile,
  systemCarouselData,
  setShowMapModalOpen,
  isTablet,
  isNewHomes,
  promotions,
  showPromotions,
  showUtilityBillUploadBanner,
  showSquareFootageEstimateBanner,
  onUtilityBillClick,
  children,
}: LayoutContentProps & { children?: React.ReactNode }) => {
  const designImages = useStorefrontDesignImages(
    offer?.designId,
    offer?.designVersion
  );

  // The large render is the only one captured with trees shown, so it is the
  // version that lets the customer see which trees are marked for removal.
  const showTreeRemovalLegend = !isNewHomes && !!offer?.treeRemovalRequired;
  const siteModelImageUrl = showTreeRemovalLegend
    ? pickDesignImageUrl(
        designImages.data?.largeImageUrl,
        designImages.data?.smallImageUrl
      )
    : pickDesignImageUrl(
        designImages.data?.smallImageUrl,
        designImages.data?.largeImageUrl
      );

  return (
    <NewContentWrapper isMobile={isMobile}>
      <SystemOverview isMobile={isMobile}>
        {showUtilityBillUploadBanner && onUtilityBillClick && (
          <OfferReviewUtilityBillUploadBanner
            onClickPrimaryAction={onUtilityBillClick}
          />
        )}
        {showSquareFootageEstimateBanner && onUtilityBillClick && (
          <OfferReviewSquareFootageEstimateBanner
            homeSizeEstimate={offer.usage.homeSizeEstimate}
            onClickPrimaryAction={onUtilityBillClick}
          />
        )}
        <SystemOverviewTitle
          size={isMobile ? 32 : 44}
          color={tokens.BRAND_HERO_BLUE}
        >
          Your System Overview
        </SystemOverviewTitle>
        <Container>
          <ContentTitle
            size={isMobile ? 20 : 28}
            color={tokens.BRAND_HERO_BLUE}
          >
            Design and equipment
          </ContentTitle>
          <SystemOverviewContent isMobile={isMobile} isTablet={isTablet}>
            {siteModelImageUrl && (
              <SolarDesignImageWrapper>
                {isNewHomes && (
                  <NewHomesDisclaimer
                    size={12}
                    color={tokens.BRAND_MEDIUM_BLUE}
                  >
                    This is a representative image and not your actual home
                  </NewHomesDisclaimer>
                )}
                <SolarDesignImage
                  bgImageUrl={isNewHomes ? bgNewHomes : siteModelImageUrl}
                  data-testid="offer-review-solar-design-image"
                />
                {showTreeRemovalLegend && <OverlaidTreeRemovalLegend />}
                <ExpandSection>
                  <ExpandImageButton
                    onClick={() => setShowMapModalOpen(true)}
                    aria-label="View solar design"
                    isNewHomes={isNewHomes}
                  >
                    <SvgIcon
                      name={SvgNames.ArrowsOutSimple}
                      width={18}
                      height={18}
                      color={tokens.BRAND_HERO_BLUE}
                    />
                  </ExpandImageButton>
                  <SystemData $cardCount={systemCarouselData.length}>
                    {systemCarouselData.map((card, index) => (
                      <Fragment key={`${card.headerText}-${index}`}>
                        <SystemDataItem>
                          <SystemDataValueRow>
                            <SystemDataText
                              size={20}
                              fontWeight="medium"
                              color={tokens.WHITE}
                            >
                              {formatCardValueWithSuffix(card)}
                            </SystemDataText>
                            {card.infoToolTipText && (
                              <SolarTooltip
                                needShift={isMobile && index % 2 !== 0}
                                content={card.infoToolTipText}
                                direction={
                                  isMobile && index % 2 !== 0
                                    ? "top-right"
                                    : "top"
                                }
                              >
                                <SvgIcon
                                  name={SvgNames.InfoCircle}
                                  width={14}
                                  height={14}
                                  color={tokens.WHITE}
                                />
                              </SolarTooltip>
                            )}
                          </SystemDataValueRow>
                          <SystemDataText size={12} color={tokens.WHITE}>
                            {card.headerText.replace("Estimated", "Est.")}
                          </SystemDataText>
                          {card.subText && (
                            <SystemDataSubText size={10} color={tokens.WHITE}>
                              {card.subText}
                            </SystemDataSubText>
                          )}
                        </SystemDataItem>
                        {index < systemCarouselData.length - 1 && <Divider />}
                      </Fragment>
                    ))}
                  </SystemData>
                </ExpandSection>
              </SolarDesignImageWrapper>
            )}
            <ProductCarousel
              products={[...offer.includedProducts, ...offer.accessoryProducts]}
              offer={offer}
            />
          </SystemOverviewContent>
        </Container>
        {offer?.financial?.summary?.warranties?.length > 0 && (
          <Warranties isMobile={isMobile}>
            <Typography
              size={isMobile ? 32 : 44}
              color={tokens.BRAND_HERO_BLUE}
            >
              Your Sunrun guarantee
              <StyledPeaceOfMindText>Have peace of mind</StyledPeaceOfMindText>
            </Typography>

            <WarrantiesList isMobile={isMobile}>
              {offer.financial.summary.warranties.map((el, index) => (
                <WarrantyListItem
                  key={`${el.title}-${el.termYears}-${index}`}
                  title={el.title}
                  termYears={el.termYears}
                  isMobile={isMobile}
                  isTablet={isTablet}
                />
              ))}
            </WarrantiesList>
          </Warranties>
        )}
        {children}
        <PriceList>
          <AgreementDetailsAndMonthlyPayments
            financialBreakdown={offer.financial}
            consumption={offer.consumption}
            production={offer.production ?? 0}
            hasBattery={hasBattery}
            nonSolarView={false}
            isAdditionalSystem={offer.additionalSystem}
            products={[...offer.includedProducts, ...offer.accessoryProducts]}
            isNewHomes={isNewHomes}
            promotions={
              showPromotions && promotions?.length ? promotions : undefined
            }
          />
        </PriceList>
      </SystemOverview>
    </NewContentWrapper>
  );
};

const NonSolarNewLayoutContent = ({
  offer,
  hasBattery,
  isMobile,
  isTablet,
  systemCarouselData,
  promotions,
  showPromotions,
  showUtilityBillUploadBanner,
  showSquareFootageEstimateBanner,
  onUtilityBillClick,
}: LayoutContentProps) => (
  <NewContentWrapper isMobile={isMobile}>
    <SystemOverview isMobile={isMobile}>
      {showUtilityBillUploadBanner && onUtilityBillClick && (
        <OfferReviewUtilityBillUploadBanner
          onClickPrimaryAction={onUtilityBillClick}
        />
      )}
      {showSquareFootageEstimateBanner && onUtilityBillClick && (
        <OfferReviewSquareFootageEstimateBanner
          homeSizeEstimate={offer.usage.homeSizeEstimate}
          onClickPrimaryAction={onUtilityBillClick}
        />
      )}
      <SystemOverviewTitle
        size={isMobile ? 32 : 44}
        color={tokens.BRAND_HERO_BLUE}
      >
        Your System Overview
      </SystemOverviewTitle>
      <Container>
        <ContentTitle size={isMobile ? 20 : 28} color={tokens.BRAND_HERO_BLUE}>
          Equipment
        </ContentTitle>
        <SystemOverviewContent
          isMobile={isMobile}
          isTablet={isTablet}
          singleColumn={true}
        >
          <ProductCarousel
            products={[...offer.includedProducts, ...offer.accessoryProducts]}
            offer={offer}
          />
          <NonSolarPaymentDetails
            financialBreakdown={offer.financial}
            consumption={offer.consumption}
            production={offer.production ?? 0}
            hasBattery={hasBattery}
            isAdditionalSystem={offer.additionalSystem}
            promotions={
              showPromotions && promotions?.length ? promotions : undefined
            }
          />
        </SystemOverviewContent>
      </Container>
    </SystemOverview>
    <Warranties isMobile={isMobile}>
      <Typography size={isMobile ? 32 : 44} color={tokens.BRAND_HERO_BLUE}>
        Your Sunrun guarantee
        <StyledPeaceOfMindText>Have peace of mind</StyledPeaceOfMindText>
      </Typography>
      <WarrantiesList isMobile={isMobile}>
        {offer.financial.summary.warranties.map((el, index) => (
          <WarrantyListItem
            key={`${el.title}-${el.termYears}-${index}`}
            title={el.title}
            termYears={el.termYears}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        ))}
      </WarrantiesList>
    </Warranties>
  </NewContentWrapper>
);

const OfferReviewContent = (props: OfferReviewContentProps) => {
  const { isNewHomes, ...rest } = props;
  return rest.nonSolarView ? (
    <NonSolarNewLayoutContent {...rest} />
  ) : (
    <SolarNewLayoutContent isNewHomes={isNewHomes} {...rest}>
      {!isNewHomes && (
        <GraphContent
          offer={rest.offer}
          prospect={rest.prospect}
          testId="Review-Page-Graph-Section"
          showSelfSupply={rest.showSelfSupply}
        />
      )}
    </SolarNewLayoutContent>
  );
};

const NewContentWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: 1005px;
  margin: 0 auto;
  padding: ${({ isMobile }) => (isMobile ? "24px 16px" : "48px 64px")};
`;

const SystemOverview = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? "24px" : "48px")};
`;

const SystemOverviewContent = styled.div<{
  isMobile: boolean;
  isTablet: boolean;
  singleColumn?: boolean;
}>`
  display: flex;
  flex-direction: ${({ isTablet, isMobile }) =>
    isTablet || isMobile ? "column" : "row"};
  gap: ${({ singleColumn }) => (singleColumn ? "64px" : "24px")};
  align-items: ${({ singleColumn }) =>
    singleColumn ? "flex-start" : "center"};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const PriceList = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
`;

const SolarDesignImageWrapper = styled.div`
  position: relative;
  flex: 1 1 0;
  min-width: 540px;
  max-width: 608px;
  height: 440px;
  border-radius: 10px;
  overflow: visible;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    flex: none;
    width: 100%;
    max-width: none;
    min-width: 0;

    /* Fluid height: 336px at 375px viewport → 440px at 768px viewport */
    --carousel-height-min: 336px;
    --carousel-height-max: 440px;
    --carousel-vw-min: 375px;
    --carousel-vw-range: 393px; /* 768 - 375 */
    --carousel-height-range: 104px; /* 440 - 336 */
    height: clamp(
      var(--carousel-height-min),
      calc(
        var(--carousel-height-min) + (100vw - var(--carousel-vw-min)) *
          var(--carousel-height-range) / var(--carousel-vw-range)
      ),
      var(--carousel-height-max)
    );
  }
`;

const SolarDesignImage = styled.div<{ bgImageUrl: string }>`
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: url(${({ bgImageUrl }) => bgImageUrl}) no-repeat center;
  background-size: cover;
`;

const OverlaidTreeRemovalLegend = styled(TreeRemovalLegend)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

const ExpandImageButton = styled.button<{ isNewHomes?: boolean }>`
  visibility: ${({ isNewHomes }) => (isNewHomes ? "hidden" : "visible")};
  align-self: flex-end;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${tokens.WHITE};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  margin-right: 16px;

  &:hover {
    opacity: 0.9;
  }
`;

const SystemOverviewTitle = styled(Typography)`
  line-height: 120% !important;
  letter-spacing: -0.88px;
`;

const ContentTitle = styled(Typography)`
  line-height: 110% !important;
  letter-spacing: -0.56px;
`;

const ExpandSection = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0) 85%
  );
  border-radius: 10px;
`;

const SystemData = styled.div<{ $cardCount: number }>`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  background-color: ${tokens.BRAND_HERO_BLUE};
  padding: 16px;
  border-radius: 0 0 8px 8px;

  @media (max-width: 649px) {
    display: grid;
    grid-template-columns: ${({ $cardCount }) =>
      $cardCount === 5 ? "1fr auto 1fr auto 1fr" : "1fr auto 1fr"};
    gap: 16px;

    > *:nth-child(${({ $cardCount }) => ($cardCount === 5 ? 6 : 4)}n) {
      display: none;
    }
  }
`;

const SystemDataItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SystemDataValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const WarrantyTooltip = styled(Tooltip)`
  .sr-tooltip-content,
  .sr-tooltip-content-mobile {
    min-width: 240px;
    max-width: 320px;
    padding: 16px;
    font-size: 12px;
    line-height: 1.4;

    @media (min-width: ${TABLET_BREAKPOINT}px) {
      padding: 24px;
      font-size: 14px;
      line-height: 20px;
    }
  }
`;

const SolarTooltip = styled(WarrantyTooltip)<{
  needShift: boolean;
  isLargeContent?: boolean;
}>`
  @media (min-width: ${TABLET_BREAKPOINT}px) {
    .sr-tooltip-content {
      width: 440px;
      min-width: 440px;
      max-width: 440px;
    }
  }

  .sr-tooltip-content,
  .sr-tooltip-content-mobile {
    transform: ${({ needShift }) => (needShift ? "none" : "translateX(-50%)")};
  }
`;

const Divider = styled.div`
  width: 1px;
  align-self: stretch;
  background-color: ${tokens.WHITE};
  flex-shrink: 0;
`;

const SystemDataText = styled(Typography)`
  text-align: center !important;
`;

const SystemDataSubText = styled(Typography)`
  text-align: center !important;
  opacity: 0.8;
`;

const Warranties = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? "16px" : "24px")};
  margin-bottom: ${({ isMobile }) => (isMobile ? "32px" : "0")};
`;

const StyledPeaceOfMindText = styled(Typography)`
  && {
    opacity: 0.7;
    font-size: 24px;
  }
`;

const WarrantiesList = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 0;
  grid-column-gap: 24px;

  ${({ isMobile }) =>
    isMobile &&
    `
  display: flex;
  flex-direction: column;
  gap: 4px;
`}
`;

const WarrantyItem = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${({ isMobile }) => (isMobile ? "16px 0" : "16px")};
  border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};
`;

const WarrantyItemContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const NewHomesDisclaimer = styled(Typography)`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 16px;
`;

export { OfferReviewContent };
